/**
 * Every page of a paginated list, as one answer (#136).
 *
 * The portal's lists draw their table, search, filters and counts from what they fetched, but
 * asked the API for one page only, its first 50 (the template lists 10): the records after it
 * could not be reached ("All 48" of 131 calls). A list endpoint built with allPagesOf asks for
 * every page in turn, and answers as one page holding them all, so the pages that read it are
 * unchanged. The answer's meta is the last page's, with the rows the API left out of every page
 * added up (meta.left_out), so that the page can say how many records it could not show.
 */

// Large enough that a list fits one or two requests; the API takes up to 10000.
export const PAGE_SIZE = 500;

/**
 * Whether a list has a page after `page`. The API answers two shapes: its own (next_page) and
 * fastapi_pagination's Page (GET /call_application/, GET /template_file/list: page, pages,
 * size, total, no next_page), where a loop on next_page alone stopped after the first page.
 */
const hasNextPage = (data, page) => {
  if (data?.next_page) return true;
  return Number.isFinite(data?.pages) && page < data.pages;
};

/**
 * A queryFn for an RTK Query endpoint: `request(arg)` gives the list's url and its own params.
 */
export const allPagesOf = (request) => {
  const queryFn = async (arg, _api, _extraOptions, baseQuery) => {
    const { url, params = {} } = request(arg);
    const items = [];
    let leftOut = 0;
    let last;
    for (let page = 1; ; page += 1) {
      const result = await baseQuery({
        url,
        method: "GET",
        params: { ...params, page, size: PAGE_SIZE },
      });
      if (result.error) return { error: result.error };
      last = result.data;
      items.push(...(last?.data?.items ?? []));
      leftOut += Number(last?.meta?.left_out ?? 0);
      if (!hasNextPage(last?.data, page)) break;
    }
    const meta =
      last?.meta?.left_out === undefined
        ? last?.meta
        : { ...last.meta, left_out: leftOut };
    return {
      data: {
        ...last,
        meta,
        data: {
          ...last?.data,
          items,
          page: 1,
          size: items.length,
          pages: 1,
          next_page: null,
          previous_page: null,
        },
      },
    };
  };
  return queryFn;
};

export default allPagesOf;
